import { FixedHeaderLayout } from "components/FixedHeaderLayout/FixedHeaderLayout"
import { useCurrentId } from "hooks/useCurrentId"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { getTintVTOService } from "services/tintvto"
import { type Brand } from "types"
import { useSaveBrandMutation } from "hooks/query/useBrandsQuery"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

interface BrandEditPageProps {
  add?: boolean
}

export const BrandPage = ({ add }: BrandEditPageProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const brandId = useCurrentId()
  const service = getTintVTOService()
  const [brand, setBrand] = useState<Brand>({
    id: brandId,
    slug: "",
    name: "",
    sub: "",
  })

  useEffect(() => {
    ;(async () => {
      if (!add && brandId) {
        const { data } = await service.brandById(brandId, "")
        setBrand({ ...data.brand })
      } else {
        setBrand({ id: "", name: "", slug: "", sub: "" })
      }
    })()
  }, [brandId, add])

  const { isPending, mutate: saveBrandMutation } = useSaveBrandMutation()

  const validateForm = () => {
    return !!(brand.name?.trim() && brand.slug?.trim() && brand.sub?.trim())
  }

  const handleSave = () => {
    saveBrandMutation(brand, {
      onSuccess: () => navigate("/brands"),
    })
  }

  const updateBrand = (patch: Partial<Brand>) => {
    setBrand((prev) => ({ ...prev, ...patch }))
  }

  return (
    <div data-testid={add ? "brand-add-page" : "brand-edit-page"}>
      <FixedHeaderLayout
        title={add ? t("brandPage.addTitle") : t("brandPage.editTitle")}
        onBack={() => navigate("/brands")}
        backLabel={t("backToBrands")}
        onFirstButtonClicked={() => navigate("/brands")}
        onSecondButtonClicked={handleSave}
        onSecondButtonLoading={isPending}
        validateSecondButton={validateForm}
      />

      <div className="p-6">
        <div className="max-w-2xl">
          <Card data-testid="brand-form-card">
            <CardHeader>
              <CardTitle className="text-base">Brand details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="name">
                    {t("name")} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    data-testid="brand-name-input"
                    value={brand.name}
                    onChange={(e) => updateBrand({ name: e.target.value })}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="slug">
                    {t("slug")} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="slug"
                    data-testid="brand-slug-input"
                    value={brand.slug}
                    onChange={(e) => updateBrand({ slug: e.target.value })}
                  />
                </div>

                <div className="col-span-2 flex flex-col gap-1.5">
                  <Label htmlFor="sub">
                    {t("sub")} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="sub"
                    data-testid="brand-sub-input"
                    value={brand.sub}
                    onChange={(e) => updateBrand({ sub: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
